/**
 * Composants de rendu/édition par type de bloc de leçon. Formulaire minimaliste
 * sur les champs principaux, persistance via onPatchData. Le split/merge/insère
 * passe par la toolbar du BlockShell.
 */

import { useRef, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import type { LessonBlockComponentProps } from './types';
import { InsertLessonFigure, appendLessonVisuals } from './insert-lesson-figure';

function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-slate-600">{label}</label>
      {children}
      {hint ? <p className="text-[11px] text-slate-500">{hint}</p> : null}
    </div>
  );
}

/**
 * Textarea du champ markdown PRINCIPAL d'un bloc. Suit la sélection courante et
 * affiche un bouton « Découper la sélection » dès qu'une plage non vide est
 * sélectionnée.
 */
function PrimaryTextArea({
  value,
  onChange,
  disabled,
  rows = 4,
  onSplitRange,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  rows?: number;
  onSplitRange?: (start: number, end: number) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [sel, setSel] = useState<{ start: number; end: number } | null>(null);

  const refreshSelection = () => {
    const el = ref.current;
    if (!el) return;
    const s = el.selectionStart ?? 0;
    const e = el.selectionEnd ?? 0;
    setSel(s !== e ? { start: Math.min(s, e), end: Math.max(s, e) } : null);
  };

  const selectedText = sel ? value.slice(sel.start, sel.end) : '';
  const canSplit = !!(sel && onSplitRange && selectedText.trim().length > 0);

  return (
    <div className="space-y-1.5">
      <textarea
        ref={ref}
        className={cn(
          'w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 font-mono text-sm',
          'focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500',
          disabled && 'opacity-60',
        )}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          requestAnimationFrame(refreshSelection);
        }}
        onSelect={refreshSelection}
        onKeyUp={refreshSelection}
        onMouseUp={refreshSelection}
        rows={rows}
        disabled={disabled}
      />
      {canSplit ? (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            if (!sel || !onSplitRange) return;
            onSplitRange(sel.start, sel.end);
            setSel(null);
          }}
          disabled={disabled}
          className="inline-flex min-h-9 items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
        >
          ✂ Découper la sélection en blocs ({sel!.end - sel!.start} car.)
        </button>
      ) : (
        <p className="text-[11px] text-slate-400">
          Astuce : sélectionnez un passage pour le découper en un nouveau bloc.
        </p>
      )}
    </div>
  );
}

export function HeadingEditor({
  block,
  isDisabled,
  onPatchData,
  onSplitRange,
}: LessonBlockComponentProps) {
  const data = block.data as { text?: string; level?: number };
  return (
    <div className="space-y-3">
      <Field label="Titre de section">
        <PrimaryTextArea
          value={data.text ?? ''}
          onChange={(v) => onPatchData({ ...data, text: v })}
          disabled={isDisabled}
          rows={1}
          onSplitRange={onSplitRange}
        />
      </Field>
      <Field label="Niveau">
        <select
          className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm"
          value={String(data.level ?? 2)}
          onChange={(e) => onPatchData({ ...data, level: Number(e.target.value) })}
          disabled={isDisabled}
        >
          <option value="1">H1 (#)</option>
          <option value="2">H2 (##)</option>
          <option value="3">H3 (###)</option>
        </select>
      </Field>
    </div>
  );
}

export function TextEditor({
  block,
  isDisabled,
  onPatchData,
  onSplitRange,
}: LessonBlockComponentProps) {
  const data = block.data as { content_md?: string };
  return (
    <div className="space-y-2">
      <Field label="Contenu (markdown)">
        <PrimaryTextArea
          value={data.content_md ?? ''}
          onChange={(v) => onPatchData({ ...data, content_md: v })}
          disabled={isDisabled}
          rows={6}
          onSplitRange={onSplitRange}
        />
      </Field>
      <InsertLessonFigure
        contextMd={data.content_md ?? ''}
        disabled={isDisabled}
        onInsert={(blocks) =>
          onPatchData({ ...data, content_md: appendLessonVisuals(data.content_md ?? '', blocks) })
        }
      />
    </div>
  );
}

export function KeyPointEditor({
  block,
  isDisabled,
  onPatchData,
  onSplitRange,
}: LessonBlockComponentProps) {
  const data = block.data as { content_md?: string; label?: string };
  return (
    <div className="space-y-3">
      <Field label="Libellé (optionnel)" hint="Ex : « À retenir », « Définition »">
        <input
          className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm"
          value={data.label ?? ''}
          onChange={(e) => onPatchData({ ...data, label: e.target.value || undefined })}
          disabled={isDisabled}
        />
      </Field>
      <Field label="Point clé (markdown)">
        <PrimaryTextArea
          value={data.content_md ?? ''}
          onChange={(v) => onPatchData({ ...data, content_md: v })}
          disabled={isDisabled}
          rows={4}
          onSplitRange={onSplitRange}
        />
      </Field>
    </div>
  );
}

export function ExampleEditor({
  block,
  isDisabled,
  onPatchData,
  onSplitRange,
}: LessonBlockComponentProps) {
  const data = block.data as { content_md?: string; title?: string };
  return (
    <div className="space-y-3">
      <Field label="Titre de l'exemple (optionnel)">
        <input
          className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm"
          value={data.title ?? ''}
          onChange={(e) => onPatchData({ ...data, title: e.target.value || undefined })}
          disabled={isDisabled}
        />
      </Field>
      <Field label="Exemple travaillé (markdown)">
        <PrimaryTextArea
          value={data.content_md ?? ''}
          onChange={(v) => onPatchData({ ...data, content_md: v })}
          disabled={isDisabled}
          rows={4}
          onSplitRange={onSplitRange}
        />
      </Field>
      <InsertLessonFigure
        contextMd={data.content_md ?? ''}
        disabled={isDisabled}
        onInsert={(blocks) =>
          onPatchData({ ...data, content_md: appendLessonVisuals(data.content_md ?? '', blocks) })
        }
      />
    </div>
  );
}

export function NoteEditor({
  block,
  isDisabled,
  onPatchData,
  onSplitRange,
}: LessonBlockComponentProps) {
  const data = block.data as { body_md?: string };
  return (
    <Field label="Note tuteur (privée, non visible élève)">
      <PrimaryTextArea
        value={data.body_md ?? ''}
        onChange={(v) => onPatchData({ ...data, body_md: v })}
        disabled={isDisabled}
        rows={3}
        onSplitRange={onSplitRange}
      />
    </Field>
  );
}

export function renderLessonBlockEditor(props: LessonBlockComponentProps) {
  switch (props.block.block_type) {
    case 'heading':
      return <HeadingEditor {...props} />;
    case 'text':
      return <TextEditor {...props} />;
    case 'key_point':
      return <KeyPointEditor {...props} />;
    case 'example':
      return <ExampleEditor {...props} />;
    case 'note':
      return <NoteEditor {...props} />;
  }
}
